import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as graphql from '@treams/treams-network';
import { Button, GraphQLErrorMessage, Modal, Paragraph, Popover, Radio } from '@treams/treams-ui';

const talentReviewVisibilityKey: { [key in graphql.TalentReviewVisibility]: string } = {
	[graphql.TalentReviewVisibility.Manager]: 'manager',
	[graphql.TalentReviewVisibility.Members]: 'everyone',
	[graphql.TalentReviewVisibility.Self]: 'self',
};

export default function ShareTalentReviewSnapshotModal(props: {
	snapshot: { id: string; visibility: graphql.TalentReviewVisibility };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const [visibility, setVisibility] = useState(props.snapshot.visibility);
	const [share, { loading, error }] = graphql.useShareTalentReviewSnapshotMutation();

	const handleShare = async () => {
		try {
			await share({ variables: { input: { id: props.snapshot.id, visibility } } });
			props.onClose();
		} catch {
			// error state is shown via GraphQLErrorMessage
		}
	};

	return (
		<Popover visible center onDismiss={props.onClose}>
			<Modal>
				<Modal.Header title={t('talentReviews.v2.snapshot.share.title')} onClose={props.onClose} />
				<Modal.Contents>
					<Paragraph mb={3}>{t('talentReviews.v2.snapshot.share.body')}</Paragraph>
					{[
						graphql.TalentReviewVisibility.Members,
						graphql.TalentReviewVisibility.Self,
						graphql.TalentReviewVisibility.Manager,
					].map((option) => {
						const translationPrefix = `talentReviews.v2.snapshot.share.visibility.${
							talentReviewVisibilityKey[option]
						}`;

						return (
							<Radio.Option
								key={option}
								checked={visibility === option}
								onChange={() => setVisibility(option)}
								title={t(`${translationPrefix}.title`)}
								description={t(`${translationPrefix}.description`)}
								maxWidth={600}
							/>
						);
					})}
					{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}
				</Modal.Contents>
				<Modal.Footer>
					<Button
						disabled={loading}
						title={t('talentReviews.v2.cancel')}
						type="secondary"
						onClick={props.onClose}
					/>
					<Button
						loading={loading}
						ml={2}
						title={t('talentReviews.v2.snapshot.share.confirm')}
						disableSuccess
						onClick={handleShare}
					/>
				</Modal.Footer>
			</Modal>
		</Popover>
	);
}
